import { useState } from 'react';

/**
 * Character-length caption fold + "see more" expand, generalized from
 * LinkedInPost's original FOLD_LENGTH pattern (the only renderer that had a
 * working expand). Facebook/Instagram/TikTok/Threads used to clamp with CSS
 * `line-clamp-N` and no way to reach the clipped text — a dead end. This
 * hook gives every clamped renderer the SAME reachable affordance instead of
 * inventing a second mechanism: slice to a per-platform fold length (tuned to
 * roughly the visual line count the renderer used to clamp to) and let the
 * caller render its own "see more" control when `needsFold && !expanded`.
 *
 * `initiallyExpanded` is for a surface where the person must read every word before deciding, such as the LinkedIn
 * review deck; a preview of how the post will look in the feed keeps the fold.
 */
export function useCaptionFold(
  caption: string | undefined,
  foldLength: number,
  initiallyExpanded = false,
) {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const body = caption ?? '';
  const needsFold = body.length > foldLength;
  const visible = expanded || !needsFold ? body : body.slice(0, foldLength).trimEnd();
  return { visible, needsFold, expanded, setExpanded };
}
